import * as React from 'react';
import {connect} from 'react-redux';

import * as Leads from 'nutshell-core/leads';
import type {Lead, LeadId, ProductMap, ProductMapPatch} from 'nutshell-core/types';

import {Button} from 'shells/button/button';
import {FlashBanner} from 'shells/flash-banner/flash-banner';

import type {NutshellState} from '../../../../store';
import {formatCurrencyValue} from '../../../../util/format-currency-value';

import {EditableProductMapRow} from './editable-product-map-row';
import {ProductMapRow} from './product-map-row';

import styles from './confirmation-form-win-lead.module.css';

// @ts-expect-error
const localeData = window.localeData;

type OwnProps = {
    onCancel?: () => any;
    onSubmit?: (
        leadId: LeadId,
        outcomeId: '1-outcomes',
        data?: {
            productMaps: ProductMapPatch[];
        }
    ) => any;
    onClearError?: () => any;
    errorMessage?: string;
    leadId: string;
    isReadOnly?: boolean;
};

type StateProps = {
    lead: Lead;
};

type DispatchProps = {
    fetchLead: (leadId: LeadId) => any;
};

type Props = OwnProps & StateProps & DispatchProps;

const mapStateToProps = function (state: NutshellState, ownProps: OwnProps): StateProps {
    return {
        // @ts-expect-error
        lead: Leads.getLead(state, ownProps.leadId),
    };
};

// @ts-expect-error
const mapDispatchToProps = function (dispatch): DispatchProps {
    return {
        fetchLead: (leadId) => {
            return dispatch(Leads.fetchLeadById(leadId));
        },
    };
};

/*
Reusable lead form component that allows the user to modify the price and
quantity of existing products on the lead, and then win the lead with those
updated prices.

Expects just a leadId, as it'll fetch the full lead model upon mounting to get
the related products.
 */
export class ConfirmationFormWinLeadComponent extends React.PureComponent<
    Props,
    {
        productMaps: ProductMap[];
    }
> {
    constructor(props: Props) {
        super(props);
        this.state = {
            productMaps: props.lead ? props.lead.productMaps : [],
        };
    }

    UNSAFE_componentWillReceiveProps(nextProps: Props) {
        if (nextProps.lead !== this.props.lead) {
            this.setState({
                productMaps: nextProps.lead.productMaps,
            });
        }
    }

    UNSAFE_componentWillMount() {
        // Don't care about the response, that comes from redux.
        this.props.fetchLead(this.props.leadId);
    }

    render() {
        const total = Leads.getLeadValueFromProductMaps(this.state.productMaps);

        // If we have any productMaps, use the first productMaps currency value
        // to format the total of all the productMaps.
        //
        // This works because all products will have the same market (leads cant
        // have multiple markets)
        const formattedTotal = this.state.productMaps.length
            ? // @ts-expect-error
              formatCurrencyValue(total, this.state.productMaps[0].price.currency)
            : `${localeData.currencySymbol}${total.toFixed(2)}`;

        return (
            <div className={styles.container}>
                {this.props.errorMessage ? (
                    <FlashBanner
                        type='error'
                        duration={6000}
                        onHidden={this.props.onClearError}
                        isOverlay={true}
                    >
                        <div>{this.props.errorMessage}</div>
                    </FlashBanner>
                ) : undefined}
                <div className={styles['table-header']}>
                    <div className={styles['product-column']}>Confirm products</div>
                    <div className={styles['qty-column']}>Qty.</div>
                    <div className={styles['price-column']}>Price</div>
                </div>
                <div className={styles['form-container']}>
                    <div className={styles['product-maps-container']}>
                        <div className={styles['product-maps']}>
                            {this.props.isReadOnly
                                ? this.state.productMaps.map((productMap) => {
                                      return (
                                          <ProductMapRow
                                              key={productMap.id}
                                              productMap={productMap}
                                          />
                                      );
                                  })
                                : this.state.productMaps.map((productMap) => {
                                      return (
                                          <EditableProductMapRow
                                              key={productMap.id}
                                              productMap={productMap}
                                              onChange={this.handleProductMapChange}
                                          />
                                      );
                                  })}
                        </div>
                        <div className={styles.total}>{formattedTotal}</div>
                    </div>
                    {this.props.isReadOnly ? undefined : (
                        <div className={styles.buttons}>
                            <Button variant='secondary' size='big' onClick={this.props.onCancel}>
                                Cancel
                            </Button>
                            <Button variant='save' size='big' onClick={this.handleSubmit}>
                                Confirm
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    /**
     * Updates the changed productMap's price and quantity, and sets our state
     * with the new updates.
     *
     * @param  {object} productMapPatch           productMapPatch object
     * @return {void}
     */
    handleProductMapChange = (productMapPatch: ProductMapPatch) => {
        this.setState((state) => Leads.updateProductMaps(state.productMaps, productMapPatch));
    };

    handleSubmit = () => {
        const data = Leads.getProductMapDataForWinningLeadPatch(this.state.productMaps);

        if (this.props.onSubmit) {
            this.props.onSubmit(this.props.leadId, '1-outcomes', data);
        }
    };
}

// @ts-expect-error
const connector = connect<Props, OwnProps, _, _, _, _>(mapStateToProps, mapDispatchToProps);
export const ConfirmationFormWinLead = connector(ConfirmationFormWinLeadComponent);
